import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import { SankeyModal } from '@/components/Modal/SankeyModal';
import { TradeDetailsModal } from '@/components/Modal/TradeDetailsModal';
import type { TradeDetails } from '@/components/Modal/TradeDetailsModal';
import type { TradeDetailsSelection } from '@/components/TradeHistoryTable/types';
import { getTradeDetailsRouteIdentity } from '@/components/TradeHistoryTable/utils';

import { SUPPORTED_CHAIN_IDS_LIMIT_ORDERS } from '@/constants/chain';

import { CheckoutMode } from '@/hooks/useCheckoutMode';
import { useTradeHistoryRoute } from '@/hooks/useTradeHistoryRoute';
import { type LimitOrderFunders, useWalletOpenLimitOrders } from '@/hooks/useWalletOpenLimitOrders';

import { MATCHA_LIMIT_ORDER } from '@/utils/liquidity-sources';
import { applyOpenLimitOrderTombstones } from '@/utils/open-limit-order-tombstones';
import { routes } from '@/utils/routes';

import { DIVIDER_CHAR } from './constants';
import { OpenOrders } from './OpenOrders';
import { OrderHistory } from './OrderHistory';
import { FiltersReducerState } from './OrderHistory/Filters/reducer';

/** Trade history content panes on /profile. */
export type HistoryView = 'swaps' | 'limit-orders' | 'open-orders';

const getTradeCheckoutMode = ({
  destinationChainId,
  chainId,
  liquiditySource,
}: {
  destinationChainId: number | undefined;
  chainId: number;
  liquiditySource: string | undefined;
}): CheckoutMode => {
  if (destinationChainId && chainId !== destinationChainId) return 'cross-chain';
  if (liquiditySource === MATCHA_LIMIT_ORDER) return 'advanced';
  return 'market';
};

/** Live open limit orders across all networks for the funders, undefined until loaded. */
export const useOpenOrdersCount = (funders: LimitOrderFunders): number | undefined => {
  const { data } = useWalletOpenLimitOrders({ funders, page: 1, limit: 1 });
  return data ? applyOpenLimitOrderTombstones(data).total : undefined;
};

export type HistoryPanelsProps = {
  /** The pane to render. */
  view: HistoryView;
  /** Wallets whose history is shown. */
  funders: LimitOrderFunders;
  /** Initial page number. */
  initialPage?: number;
  /** Initial filters. */
  initialFilters?: FiltersReducerState;
  /** Initial search term. */
  initialSearchTerm?: string;
  /** Initial open order chain ID for persistence in URL. null selects all networks. */
  initialOpenOrderChainId: number | null;
  /**
   * Query params identifying the current view, kept when filters are reset.
   * The host route owns how a view is addressed in the URL.
   */
  viewQuery: Record<string, string | string[]>;
};

/**
 * Renders one trade-history pane along with the trade details modals, and
 * keeps pagination, filters and search in the URL query.
 */
export const HistoryPanels = ({
  view,
  funders,
  initialPage,
  initialFilters,
  initialSearchTerm,
  initialOpenOrderChainId,
  viewQuery,
}: HistoryPanelsProps) => {
  const router = useRouter();
  const [selectedTrade, setSelectedTradeDetails] = useState<TradeDetails | undefined>();
  const [isSankeyModalOpen, setIsSankeyModalOpen] = useState(false);
  const [openOrderChainId, setOpenOrderChainId] = useState<number | null>(initialOpenOrderChainId);

  // Follow the networks query param, which back/forward changes without remounting this pane.
  const queryNetworks =
    typeof router.query.networks === 'string' ? router.query.networks : undefined;
  useEffect(() => {
    if (!queryNetworks) {
      setOpenOrderChainId(null);
      return;
    }

    const supportedChainId = queryNetworks
      .split(DIVIDER_CHAR)
      .map(Number)
      .find(
        (chainId) => Number.isFinite(chainId) && SUPPORTED_CHAIN_IDS_LIMIT_ORDERS.includes(chainId),
      );

    setOpenOrderChainId(supportedChainId ?? null);
  }, [queryNetworks]);

  const handleShowTradeDetails = (selectedTrade: TradeDetailsSelection) => {
    const routeIdentity = getTradeDetailsRouteIdentity(selectedTrade);
    if (!routeIdentity) return;
    router.push({
      pathname: routes.TX,
      query: {
        chainId: String(selectedTrade.chainId),
        txHash: routeIdentity.txHash,
        mode: getTradeCheckoutMode({
          destinationChainId: selectedTrade.destinationChainId,
          chainId: selectedTrade.chainId,
          liquiditySource: selectedTrade.liquiditySource,
        }),
        ...(routeIdentity.funderAddress ? { funderAddress: routeIdentity.funderAddress } : {}),
      },
    });
  };

  const handleCloseTradeDetails = () => setSelectedTradeDetails(undefined);

  const handleShowSankeyDiagram = () => setIsSankeyModalOpen(true);

  const handleOnPageChange = useCallback(
    (page: number) => {
      const query = { ...router.query };
      if (page === 1) {
        delete query.page;
      } else {
        query.page = page.toString();
      }
      router.push({ query }, undefined, { shallow: true });
    },
    [router],
  );

  const handleOnFiltersChange = (filters: FiltersReducerState) => {
    const { time, networks, orderTypes, status } = filters;

    const query = { ...router.query };
    delete query.page;

    if (time) {
      query.time = time.toString();
    } else {
      delete query.time;
    }
    if (networks.length) {
      query.networks = networks.join(DIVIDER_CHAR);
    } else {
      delete query.networks;
    }
    if (orderTypes.length) {
      query.orderTypes = orderTypes.join(DIVIDER_CHAR);
    } else {
      delete query.orderTypes;
    }
    if (status) {
      query.advancedOrderStatus = status;
    } else {
      delete query.advancedOrderStatus;
    }

    router.push({ query }, undefined, { shallow: true });
  };

  const handleOnSearchTermChange = useCallback(
    (searchTerm: string) => {
      const query = { ...router.query };
      if (searchTerm === '') {
        delete query.searchTerm;
      } else {
        query.searchTerm = searchTerm;
      }
      router.push({ query }, undefined, { shallow: true });
    },
    [router],
  );

  const handleOnFiltersReset = () => {
    router.push({ query: viewQuery }, undefined, { shallow: true });
  };

  const handleOnOpenOrderChainChange = useCallback(
    (newChainId: number | null) => {
      setOpenOrderChainId(newChainId);
      const query = { ...router.query };

      if (newChainId === null) {
        delete query.networks;
      } else {
        query.networks = newChainId.toString();
      }

      router.push({ query }, undefined, { shallow: true });
    },
    [router],
  );

  // fetch trade route
  const { data: routeData } = useTradeHistoryRoute({
    transactionHash: selectedTrade?.transactionHash,
    tradeId: selectedTrade?.tradeId,
    protocolVersion: selectedTrade?.protocolVersion,
  });

  const orderHistoryProps = {
    funders,
    handleShowTradeDetails,
    initialPage,
    initialFilters,
    initialSearchTerm,
    onPageChange: handleOnPageChange,
    onFiltersChange: handleOnFiltersChange,
    onSearchTermChange: handleOnSearchTermChange,
    onFiltersReset: handleOnFiltersReset,
  };

  return (
    <>
      {view === 'swaps' && <OrderHistory {...orderHistoryProps} orderType="swaps" />}
      {view === 'limit-orders' && <OrderHistory {...orderHistoryProps} orderType="limit" />}
      {view === 'open-orders' && (
        <OpenOrders
          funders={funders}
          openOrderChainId={openOrderChainId}
          onOpenOrderChainChange={handleOnOpenOrderChainChange}
          setSelectedTradeDetails={setSelectedTradeDetails}
        />
      )}
      {selectedTrade && (
        <TradeDetailsModal
          isOpen={!!selectedTrade}
          onClose={handleCloseTradeDetails}
          onOpenChange={handleCloseTradeDetails}
          trade={selectedTrade}
          route={routeData?.route}
          handleShowSankeyDiagram={handleShowSankeyDiagram}
        />
      )}
      {selectedTrade && routeData?.route && (
        <span className="m-4">
          <SankeyModal
            route={routeData.route}
            chainId={Number(routeData.chainId)}
            isOpen={isSankeyModalOpen}
            onClose={() => setIsSankeyModalOpen(false)}
          />
        </span>
      )}
    </>
  );
};
